import Cookies from 'js-cookie'
import { NextPageContext } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import AddressHero from 'modules/AddressAutocomplete/AddressHero'
import AddressSection from 'modules/AddressAutocomplete/AddressSection'
import ComparisonChart from 'modules/Marketing/ComparisonChart'
import FeaturedOnMedia from 'modules/Marketing/FeaturedOnMedia'
import PricingPackages from 'modules/Marketing/PricingPackages'
import Results from 'modules/Marketing/Results'
import MarketingSalePriceSlider from 'modules/Marketing/SalePriceSlider'
import Testimonials from 'modules/Marketing/Testimonials'

import DisclaimerText from 'components/DisclaimerText'
import Section from 'components/Section'
import Spacer from 'components/Spacer'

import ApiCreator from 'services/api'
import { usePricingPackagesForState } from 'services/swr/pricingPackages/usePricingPackagesForState'
import { useUser } from 'services/swr/useUser'

import CampaignData from 'data/MarketingData/CampaignData'
import StateCampaignData from 'data/MarketingData/StateCampaignData'

import * as T from 'types'
import * as E from 'types/enums'

const StyledDisclaimerText = styled(DisclaimerText)`
  position: absolute;
  bottom: 15px;
`

const ACTIVE_CAMPAIGNS = { ...CampaignData, ...StateCampaignData }

const DISCLAIMER_TEXT = '*Availability varies by location'

interface IProps {
  states: T.IState[]
}

const HomeOtherCampaigns: FunctionComponent<IProps> = ({ states }) => {
  // theme
  const theme = useContext(ThemeContext)
  const { user } = useUser()

  // @TEST ME make sure I'm working with gclid
  // query for appending UTM params
  // const { params: query } = useContext(QueryParamsContext)

  const router = useRouter()
  const pathname = router.query.campaign as string
  let campaign = ACTIVE_CAMPAIGNS[pathname]

  const currentState = states.find(state => {
    // remove all spaces, commas, and periods
    const stateName = state.name.replace(/[ ,.]+/g, '').toLowerCase()
    return stateName === pathname.replace('-', '')
  })

  useEffect(() => {
    if (currentState) Cookies.set('state', currentState.abbreviation)
  }, [currentState])

  // @TODO Once we are able to get the user's location we shouldn't
  // have to rely on a state's fallback default packages.
  // For context: We need a state to return pricing packages. CA state doesn't have state specific packages
  // so that will use the default fallback packages. We need default packages for marketing pages
  // in which we have no states to choose from.
  const { statePricingPackages } = usePricingPackagesForState(currentState?.abbreviation ?? 'CA')

  const [isFreeToList, setIsFreeToList] = useState<boolean>(true)

  useEffect(() => {
    if (!statePricingPackages) return

    const basicPkg = statePricingPackages.find(pkg => pkg.name === E.PackageType.basic)

    setIsFreeToList(basicPkg?.payNow === 0)
  }, [statePricingPackages])

  useEffect(() => {
    campaign = ACTIVE_CAMPAIGNS[pathname]
    // redirect to the homepage if it is an invalid campaign route
    if (router.asPath !== '/d/[campaign]' && !campaign) router.push('/')
  }, [router.asPath])

  if (!campaign || !statePricingPackages) return <Spacer size={1000} />

  return (
    <>
      <Head>
        <title>{campaign.pageTitle}</title>
        <meta
          name="description"
          content={campaign.metaDescription}
        />
        <meta
          name="keywords"
          content={campaign.metaKeywords}
        />
      </Head>
      <Section noPadding>
        <AddressHero
          title={campaign.heroTitle}
          subtitle={campaign.heroSubtitle}
          imgSrc={`${process.env.CDN_BASE_URL}/q_auto/${campaign.imgUrl}`}
          mobileImgSrc={`${process.env.CDN_BASE_URL}/q_auto,w_1440/${campaign.imgUrl}`}
          freeToList={isFreeToList}
        />
        {isFreeToList && (
          <StyledDisclaimerText themeColor="white">{DISCLAIMER_TEXT}</StyledDisclaimerText>
        )}
      </Section>
      {/* FEATURED ON MEDIA */}
      <FeaturedOnMedia backgroundColor={theme.colors.white} />
      {/* PRICING COMPARISON SLIDER */}
      <Section
        mobilePadding="40px"
        tabletPadding="40px"
        desktopPadding="50px"
        backgroundColor={theme.colors.grey2}
        column
      >
        <MarketingSalePriceSlider
          defaultPrice={campaign.stateMedianHomePrice}
          color={theme.colors.blue}
          fillHeight={8}
        />
      </Section>
      {/* COMPARISON CHART */}
      <ComparisonChart
        title={campaign.infoTitle}
        content={campaign.infoContent}
        campaignType={pathname}
        freeToList={isFreeToList}
      />
      {/* RESULTS */}
      <Results />
      {/* TESTIMONIALS */}
      <Section
        tabletPadding="40px"
        desktopPadding="50px"
        column
      >
        <Testimonials
          campaignType={pathname}
          stateCampaign={campaign.stateCampaign}
        />
      </Section>
      {/* PRICING PACKAGE */}
      <Section
        backgroundColor={theme.colors.grey2}
        desktopPadding="50px"
        column
      >
        <PricingPackages
          pricingPackages={statePricingPackages}
          selectedState={currentState}
        />
      </Section>
      {/* SIGN UP */}
      {!user && (
        <Section
          backgroundImage={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/c/1920/homelister-map.jpg`}
          desktopPadding="50px"
          column
        >
          <AddressSection />
        </Section>
      )}
    </>
  )
}

export const getServerSideProps = async (ctx: NextPageContext) => {
  try {
    const api = new ApiCreator(ctx)
    const statesRes = await api.getStates()

    if (!statesRes.ok) {
      throw new Error('We were unable to load states at this time.')
    }

    return {
      props: {
        states: statesRes.data,
      },
    }
  } catch (err: any) {
    const error = err as Error

    return { props: { serverFetchError: error.message } }
  }
}

export default HomeOtherCampaigns
